import { DateTime } from 'luxon';

import type { Props } from './types';
import type { FilterTimeType } from '@/app/types/Filter';
import type { DateTimeUnit, DurationObjectUnits } from 'luxon';

export const useHandlers = ({ value, onChange }: Props) => {
	// Placeholder for future handler logic

	const timeType = value?.timeType ?? 'daily';

	const startTime = value?.startTime
		? DateTime.fromISO(value.startTime)
		: DateTime.now().startOf('day');

	const endTime = value?.endTime ? DateTime.fromISO(value.endTime) : DateTime.now().endOf('day');

	const headerMapper = {
		weekly: `WEEK ${startTime?.weekNumber ?? ''}`,
		monthly: `${startTime?.toFormat('yyyy')}`,
		custom: 'CUSTOM',
	};

	const resolveHeaderName = () => {
		if (timeType === 'daily') {
			return startTime.hasSame(DateTime.now(), 'day')
				? 'TODAY'
				: startTime.toFormat('cccc').toUpperCase();
		}
		return headerMapper[timeType];
	};

	const headerValueMapper = {
		daily: `${startTime.toFormat('dd LLL')}`,
		weekly: `${startTime.toFormat('dd')} - ${endTime.toFormat('dd')}`,
		monthly: `${startTime.toFormat('LLLL')}`,
		custom: `${startTime.toFormat('dd LLL')} - ${endTime.toFormat('dd LLL')}`,
	};

	const changeUnitMapper: Record<FilterTimeType, keyof DurationObjectUnits> = {
		daily: 'days',
		weekly: 'weeks',
		monthly: 'months',
		custom: 'days',
	};

	const periodStartOfMapper: Record<FilterTimeType, DateTimeUnit> = {
		daily: 'day',
		weekly: 'week',
		monthly: 'month',
		custom: 'day',
	};

	const handleChange = (amount: number) => {
		if (startTime.isValid === false || endTime.isValid === false) {
			return;
		}

		onChange?.({
			timeType,
			startTime: startTime
				.plus({ [changeUnitMapper[timeType]]: amount })
				.startOf(periodStartOfMapper[timeType], {
					useLocaleWeeks: true,
				})
				.toISO(),
			endTime: endTime
				.plus({ [changeUnitMapper[timeType]]: amount })
				.endOf(periodStartOfMapper[timeType], { useLocaleWeeks: true })
				.toISO(),
		});
	};

	return {
		header: resolveHeaderName(),
		headerValue: headerValueMapper[timeType],
		timeType,
		handleChange,
	};
};
